import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2, ThumbsUp, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { Gate } from '../../AccessStudio';
import BehaviorIncidentModal from './BehaviorIncidentModal';
import useScopedClasses from '../useScopedClasses';

// Behavior log grouped per student: one row per student with
// incident/merit counts; click to expand their individual records.
const BehaviorLog = ({ apiCall, year, showSuccess, showError }) => {
  const { visibleClasses, className, setClassName } = useScopedClasses(apiCall, year);
  const [sections, setSections] = useState([]);
  const [sectionCode, setSectionCode] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [records, setRecords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState(null); // StudentID

  useEffect(() => {
    if (!className) return;
    apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`)
      .then(s => setSections(s.filter(x => x.IsActive)))
      .catch(() => setSections([]));
    setSectionCode('');
  }, [apiCall, year, className]);

  const load = useCallback(async () => {
    if (!className) return;
    try {
      let url = `/academic/behavior?academicYear=${year}&className=${encodeURIComponent(className)}`;
      if (sectionCode) url += `&sectionCode=${encodeURIComponent(sectionCode)}`;
      if (typeFilter) url += `&recordType=${typeFilter}`;
      setRecords(await apiCall(url));
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, sectionCode, typeFilter, showError]);

  useEffect(() => { load(); }, [load]);

  // One entry per student, records newest-first, sorted by name
  const students = useMemo(() => {
    const map = new Map();
    for (const r of records) {
      if (!map.has(r.StudentID)) {
        map.set(r.StudentID, {
          studentId: r.StudentID, firstName: r.FirstName, lastName: r.LastName,
          class: r.Class, section: r.Section, records: [], incidents: 0, merits: 0
        });
      }
      const s = map.get(r.StudentID);
      s.records.push(r);
      if (r.RecordType === 'Merit') s.merits++; else s.incidents++;
    }
    return [...map.values()].sort((a, b) =>
      `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
  }, [records]);

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete this ${r.RecordType.toLowerCase()} for ${r.FirstName} ${r.LastName}?`)) return;
    try {
      await apiCall(`/academic/behavior/${r.BehaviorID}`, { method: 'DELETE' });
      showSuccess('Record deleted');
      load();
    } catch (err) { showError(err.message); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <select value={className} onChange={e => setClassName(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {visibleClasses.length === 0 && <option value="">No classes</option>}
          {visibleClasses.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
        </select>
        <select value={sectionCode} onChange={e => setSectionCode(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All sections</option>
          {sections.map(s => <option key={s.SectionID} value={s.SectionCode}>{className} {s.SectionCode}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">Incidents + Merits</option>
          <option value="Incident">Incidents only</option>
          <option value="Merit">Merits only</option>
        </select>
        <div className="flex-1" />
        <Gate permission="academic.behavior.record" label="Log Behavior Record">
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus size={15} /> New Record
          </button>
        </Gate>
      </div>

      <div className="space-y-2">
        {students.map(s => {
          const open = expanded === s.studentId;
          return (
            <div key={s.studentId} className="bg-white rounded-lg shadow">
              {/* Student row: counts summary, click to expand */}
              <button
                onClick={() => setExpanded(open ? null : s.studentId)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 rounded-lg"
              >
                {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                <span className="font-medium text-gray-800 text-sm">{s.firstName} {s.lastName}</span>
                <span className="text-xs text-gray-400">{s.class} {s.section}</span>
                <span className="flex-1" />
                {s.incidents > 0 && (
                  <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    <AlertTriangle size={11} /> {s.incidents} incident{s.incidents > 1 ? 's' : ''}
                  </span>
                )}
                {s.merits > 0 && (
                  <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    <ThumbsUp size={11} /> {s.merits} merit{s.merits > 1 ? 's' : ''}
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  last: {new Date(s.records[0].RecordDate).toLocaleDateString()}
                </span>
              </button>

              {/* Expanded: the student's individual records */}
              {open && (
                <div className="px-4 pb-3 space-y-2">
                  {s.records.map(r => (
                    <div key={r.BehaviorID}
                         className={`rounded-lg border-l-4 bg-gray-50 px-3 py-2 ${r.RecordType === 'Merit' ? 'border-green-500' : 'border-red-500'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-gray-800 flex items-center gap-2">
                            {r.RecordType === 'Merit'
                              ? <ThumbsUp size={13} className="text-green-600" />
                              : <AlertTriangle size={13} className="text-red-600" />}
                            <span className="font-medium">{r.RecordType}</span>
                            {r.Category && <span className="text-xs bg-white border text-gray-600 px-2 py-0.5 rounded-full">{r.Category}</span>}
                          </p>
                          {r.Description && <p className="text-sm text-gray-600 mt-1">{r.Description}</p>}
                          {r.ActionTaken && <p className="text-xs text-gray-500 mt-0.5">Action: {r.ActionTaken}</p>}
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <p className="text-xs text-gray-500">{new Date(r.RecordDate).toLocaleDateString()}</p>
                          {r.RecordedByName && <p className="text-xs text-gray-400">{r.RecordedByName}</p>}
                          <Gate permission="academic.behavior.record" label="Delete Behavior Record">
                            <button onClick={() => handleDelete(r)} className="text-red-400 hover:text-red-600 mt-1"><Trash2 size={14} /></button>
                          </Gate>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {students.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 text-sm">No behavior records match.</div>
        )}
      </div>

      {showModal && (
        <BehaviorIncidentModal
          apiCall={apiCall} year={year} className={className} sectionCode={sectionCode}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
          showSuccess={showSuccess} showError={showError}
        />
      )}
    </div>
  );
};

export default BehaviorLog;
